import { useRef } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { LocationSummaryData, OperatorDashboardPeriod } from '@evi/shared';
import { fetchLocationSummary } from '@/lib/api';

/**
 * React-Query-backed hook for the per-location dashboard summary.
 * Sibling to `useOperatorSummary`; same staleness / live shape
 * scoped to one location instead of the tenant.
 *
 * `period` defaults to '7d' matching the admin landing default
 * (e1f7e91). The KPI fields in the response are window-independent
 * (revenueToday, energyMtd, chargersOnline, etc.); only the
 * revenueLast24h bucket array varies with period. Phase 2 of the
 * LocationInfoCard expansion consumes just the KPI fields; Phase 3
 * will lift period control up to the card so the embedded chart
 * can toggle.
 *
 * `enabled` gate prevents the query from firing when the card
 * isn't open (locationId === null / empty string).
 */
export function useLocationSummary(
  locationId: string | null,
  period: OperatorDashboardPeriod = '7d',
) {
  // Track the previous locationId across renders so we can decide
  // whether keepPreviousData is the right placeholder. Option A from
  // the Phase 4 verification note (ba1a053):
  //   - Same locationId, period changed → keepPreviousData
  //     (KPI fields are window-independent; chart bucket array
  //      flashes to the old period's array briefly — acceptable)
  //   - Different locationId → DO NOT carry prior data, since the
  //     KPIs would briefly show the WRONG LOCATION's numbers (a
  //     ~100-300ms wrong-content flash that's worse than the em-dash
  //     loading state we replace it with).
  // The ref is read BEFORE assigning the new value so the comparison
  // uses last render's locationId.
  const prevLocationIdRef = useRef<string | null>(locationId);
  const isSameLocation = prevLocationIdRef.current === locationId;
  // Update for the next render's comparison.
  prevLocationIdRef.current = locationId;

  return useQuery<LocationSummaryData>({
    queryKey: ['locations', locationId ?? '__none__', 'summary', period],
    queryFn: () => fetchLocationSummary(locationId as string, period),
    enabled: Boolean(locationId),
    staleTime: 30_000,
    // Live: session and charger events refetch it (LIVE_QUERY_KEYS); no poll.
    // Period flips on the SAME location: hold previous response so
    // KPI tiles don't flash to placeholders for the request
    // roundtrip. KPI fields are window-independent (revenueToday,
    // energyMtd, chargersOnline) so the held values stay valid
    // until the new ones land.
    //
    // Location switch: drop placeholderData so the new card mount
    // doesn't briefly render the previous location's KPIs. Em-dash
    // / "loading…" flash is the correct UX for a switch — the
    // wrong-content flash was the bug Phase 4 verification (ba1a053)
    // flagged.
    placeholderData: isSameLocation ? keepPreviousData : undefined,
  });
}
